import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /**
   * Keeps `cart-summary-sticky` in sync with the footer's actual rendered
   * size for as long as the drawer stays open — a one-shot measurement taken
   * only at open time goes stale the moment content changes underneath it
   * (an item morphed in after auto-open, the Frequently Bought Together
   * carousel loading in, or the "Choose Your Impact" accordion expanding),
   * which is what made the drawer intermittently un-scrollable or shift its
   * footer around after those changes.
   * @type {ResizeObserver | null}
   */
  #resizeObserver = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
    this.#resizeObserver?.disconnect();
  }

  /**
   * Runs the sticky-state calculation on open, then keeps it live for as
   * long as the drawer's content can still change size.
   */
  #handleDialogOpen = () => {
    this.#updateStickyState();
    this.#observeSizeChanges();
  };

  /**
   * (Re)binds a ResizeObserver to the currently-rendered dialog/footer so a
   * later content change — a morphed-in line item, the upsell carousel, or
   * the impact accordion opening — recomputes the sticky state instead of
   * leaving it pinned to whatever was measured when the drawer opened.
   */
  #observeSizeChanges() {
    this.#resizeObserver?.disconnect();

    const { dialog } = /** @type {Refs} */ (this.refs);
    const footer = dialog?.querySelector('.cart-drawer__footer');

    if (!dialog || !footer) return;

    this.#resizeObserver = new ResizeObserver(this.#updateStickyState);
    this.#resizeObserver.observe(dialog);
    this.#resizeObserver.observe(footer);
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open') && this.#shouldAutoOpenFor(event)) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Whether an event on the shared `cart:update` channel is an add that should
   * pop the drawer open.
   * @param {Event} event
   * @returns {boolean}
   */
  #shouldAutoOpenFor(event) {
    // Quantity changes and removals ride the same event name via
    // CartUpdateEvent. Auto-opening on those would pop the drawer every time
    // someone edited a line on the cart page.
    if (!(event instanceof CartAddEvent)) return false;

    // A rejected add put nothing in the cart, and the post-add sync only
    // re-announces an add already handled — neither should re-open a drawer the
    // shopper has since dismissed.
    const { didError, source } = /** @type {CartAddEvent} */ (event).detail.data ?? {};
    if (didError || source === 'product-form-sync') return false;

    // The header renders a second time inside the mobile menu drawer, so every
    // page carries two cart drawers. Only the one actually on screen may open:
    // a hidden copy that opens stays modal after the visible one is dismissed,
    // and a modal dialog makes the rest of the page inert — which left the
    // add-to-cart button dead until a reload.
    if (!this.#isRendered()) return false;

    // Never stack a second drawer on top of one that is already open.
    return !document.querySelector('cart-drawer-component dialog[open]');
  }

  /**
   * Whether this drawer is actually rendered, as opposed to being the duplicate
   * copy inside the collapsed menu drawer.
   *
   * `checkVisibility()` is the test that holds at both breakpoints —
   * `offsetParent` is non-null for the menu drawer's copy on mobile even while
   * its `<details>` container is closed.
   * @returns {boolean}
   */
  #isRendered() {
    return typeof this.checkVisibility === 'function' ? this.checkVisibility() : this.offsetParent !== null;
  }

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState = () => {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    // The whole footer band, not just the totals — it also holds the "Choose
    // Your Impact" accordion, and that accordion's expanded height has to
    // count toward the ratio or opening it can push the footer taller than
    // the drawer while still being (wrongly) treated as sticky.
    const footer = dialog.querySelector('.cart-drawer__footer');

    if (!content || !footer) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when the footer disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const footerHeight = footer.getBoundingClientRect().height;
    const ratio = footerHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  };
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
